import React from 'react';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';

import EgsCategoryColumn from './EgsCategoryColumn';
import EgsRoomScopeControl from './EgsRoomScopeControl';
import { EGS_CATS } from './fields';
import type { EgsDraft, EgsDraftInput, EgsPackageOption } from './types';
import { useEgsWizardModalState } from './useEgsWizardModalState';

interface Props {
  audToSourceCurrencyRate: Record<string, number | undefined>;
  availablePackageOptions: Array<EgsPackageOption>;
  currency: string;
  editingDraft?: EgsDraft;
  isFxRateLoading: boolean;
  onClose: () => void;
  onCommit: (records: Array<EgsDraftInput>) => void;
  open: boolean;
  roomTypes: Array<string>;
}

export default function EgsWizardModal({
  audToSourceCurrencyRate,
  availablePackageOptions,
  currency,
  editingDraft,
  isFxRateLoading,
  onClose,
  onCommit,
  open,
  roomTypes,
}: Props) {
  const state = useEgsWizardModalState({
    audToSourceCurrencyRate,
    availablePackageOptions,
    currency,
    editingDraft,
    isFxRateLoading,
    onClose,
    onCommit,
    open,
    roomTypes,
  });

  if (!state) {
    return null;
  }

  const {
    alreadySet,
    draft,
    error,
    handleBack,
    handleClose,
    handleNext,
    hasHistory,
    hasMoreRoomsAfterThis,
    roomCompleted,
    roomIndex,
    roomName,
    toggleCategory,
    updateCategory,
    updateRoomDraft,
  } = state;

  return (
    <Dialog fullWidth maxWidth="lg" onClose={handleClose} open={open}>
      <DialogTitle>
        {editingDraft ? 'Edit Extra Guest Surcharge' : 'Extra Guest Surcharges'}
        {!editingDraft && (
          <Typography color="text.secondary" component="div" variant="caption">
            {`Room ${roomIndex + 1} of ${roomTypes.length}`}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography fontWeight={600} variant="h6">
            {roomName}
          </Typography>

          {!editingDraft && alreadySet.length > 0 && (
            <Alert severity="success">
              {`Already set for this room: ${alreadySet.map((meta) => meta.label).join(', ')}.`}
            </Alert>
          )}

          {error && <Alert severity="warning">{error}</Alert>}

          {!editingDraft && (
            <>
              <EgsRoomScopeControl
                currentRoom={roomName}
                mode={draft.globalApplyMode}
                onModeChange={(mode) => updateRoomDraft({ globalApplyMode: mode })}
                onRoomsChange={(globalApplyRooms) => updateRoomDraft({ globalApplyRooms })}
                rooms={roomTypes}
                selectedRooms={draft.globalApplyRooms}
                title="Set these EGS rules for"
              />

              <Divider />
            </>
          )}

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            {EGS_CATS.map((meta) => (
              <EgsCategoryColumn
                availablePackageOptions={availablePackageOptions}
                category={draft[meta.key]}
                currency={currency}
                currentRoom={roomName}
                key={meta.key}
                locked={roomCompleted[meta.key]}
                meta={meta}
                onChange={(changes) => updateCategory(meta.key, changes)}
                onToggleEnabled={() => toggleCategory(meta.key)}
                roomTypes={roomTypes}
              />
            ))}
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="text">
          Cancel
        </Button>
        {hasHistory && (
          <Button onClick={handleBack} variant="text">
            Back
          </Button>
        )}
        <Button color="primary" onClick={handleNext} variant="contained">
          {hasMoreRoomsAfterThis ? 'Next' : 'Finish'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
